import React from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useTheme } from '../../contexts/ThemeContext';
import * as SliderPrimitive from '@radix-ui/react-slider';

interface SettingsPanelProps {
  confThreshold: number;
  onConfThresholdChange: (value: number) => void;
  pixelToUm: number;
  onPixelToUmChange: (value: number) => void;
}

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  confThreshold,
  onConfThresholdChange,
  pixelToUm,
  onPixelToUmChange,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Get color based on confidence threshold value
  const getConfidenceColor = (value: number) => {
    if (value < 0.3) {
      return {
        primary: '#22c55e', // green-500
        light: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
        border: isDark ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)',
        text: isDark ? '#4ade80' : '#16a34a',
        glow: 'rgba(34, 197, 94, 0.4)',
      };
    } else if (value < 0.6) {
      return {
        primary: '#f59e0b', // amber-500
        light: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
        border: isDark ? 'rgba(245, 158, 11, 0.4)' : 'rgba(245, 158, 11, 0.3)',
        text: isDark ? '#fbbf24' : '#d97706',
        glow: 'rgba(245, 158, 11, 0.4)',
      };
    } else {
      return {
        primary: '#ef4444', // red-500
        light: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.15)',
        border: isDark ? 'rgba(239, 68, 68, 0.4)' : 'rgba(239, 68, 68, 0.3)',
        text: isDark ? '#f87171' : '#dc2626',
        glow: 'rgba(239, 68, 68, 0.4)',
      };
    }
  };

  const confidenceColor = getConfidenceColor(confThreshold);

  return (
    <div className="space-y-4 p-3 bg-muted/30 dark:bg-gray-800/50 rounded-lg">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
        <SlidersHorizontal className="w-3 h-3 text-primary" />
        Microsphere Detection Settings
      </h3>

      {/* Confidence Threshold with dynamic color feedback */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs">Confidence Threshold</Label>
          <span
            className="text-xs font-mono font-semibold px-2.5 py-1 rounded-md"
            style={{
              background: `linear-gradient(135deg, ${confidenceColor.light} 0%, ${confidenceColor.light} 100%)`,
              color: confidenceColor.text,
              border: `1px solid ${confidenceColor.border}`,
              boxShadow: `0 0 8px ${confidenceColor.glow}`,
            }}
          >
            {(confThreshold * 100).toFixed(0)}%
          </span>
        </div>

        {/* Custom slider with dynamic color */}
        <SliderPrimitive.Root
          min={0}
          max={100}
          step={1}
          value={[confThreshold * 100]}
          onValueChange={(value: number[]) => onConfThresholdChange(value[0] / 100)}
          className="relative flex w-full touch-none select-none items-center py-1"
        >
          <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
            <SliderPrimitive.Range
              className="absolute h-full"
              style={{
                background: confidenceColor.primary,
                boxShadow: `0 0 12px ${confidenceColor.glow}`,
              }}
            />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            className="block h-5 w-5 rounded-full bg-background ring-offset-background transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-grab active:cursor-grabbing hover:scale-110"
            style={{
              border: `2px solid ${confidenceColor.primary}`,
              boxShadow: `0 2px 8px rgba(0,0,0,0.2), 0 0 12px ${confidenceColor.glow}`,
            }}
          />
        </SliderPrimitive.Root>

        <div className="flex justify-between text-[10px] text-muted-foreground">
          <span>More results</span>
          <span>Higher quality</span>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Higher = fewer but more confident detections
        </p>
      </div>

      {/* Pixel Calibration */}
      <div className="space-y-2">
        <Label className="text-xs">Pixel Calibration (um/px)</Label>
        <Input
          type="number"
          min={0.01}
          max={100}
          step={0.01}
          value={pixelToUm}
          onChange={(e) => onPixelToUmChange(Number(e.target.value) || 1.3)}
          className="h-8"
        />
      </div>

      {/* Reset button for all settings */}
      <Button
        variant="outline"
        size="sm"
        className="w-full h-8 text-xs"
        onClick={() => {
          onConfThresholdChange(0.25);
          onPixelToUmChange(1.3);
        }}
      >
        Reset to Defaults
      </Button>
    </div>
  );
};
